import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '../lib/errors';

interface Comunicacao {
  id: number;
  tipo: string;
  direcao: string;
  assunto: string | null;
  conteudo: string | null;
  destinatario_email: string | null;
  data_envio: string;
  lida: boolean;
  anexos: any[];
}

const TIPO_CONFIG: Record<string, { icon: string; label: string; tone: string }> = {
  email: { icon: 'ri-mail-line', label: 'E-mail', tone: 'bg-blue-50 text-blue-700' },
  ligacao: { icon: 'ri-phone-line', label: 'Ligação', tone: 'bg-green-50 text-green-700' },
  whatsapp: { icon: 'ri-whatsapp-line', label: 'WhatsApp', tone: 'bg-emerald-50 text-emerald-700' },
  nota: { icon: 'ri-sticky-note-line', label: 'Nota', tone: 'bg-gray-100 text-gray-700' },
  reuniao: { icon: 'ri-team-line', label: 'Reunião', tone: 'bg-indigo-50 text-indigo-700' },
};

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const hoje = new Date();
  const diff = Math.ceil((hoje.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
  if (diff === 0) return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  if (diff === 1) return `Ontem ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

interface TimelineComunicacoesProps {
  empresaId: number;
  onDataChanged?: () => void;
}

export default function TimelineComunicacoes({ empresaId, onDataChanged }: TimelineComunicacoesProps) {
  const [comunicacoes, setComunicacoes] = useState<Comunicacao[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [mostrarForm, setMostrarForm] = useState(false);
  const [tipoForm, setTipoForm] = useState('email');
  const [assuntoForm, setAssuntoForm] = useState('');
  const [conteudoForm, setConteudoForm] = useState('');
  const [enviando, setEnviando] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: e } = await supabase
        .from('comunicacoes')
        .select('*')
        .eq('empresa_id', empresaId)
        .order('data_envio', { ascending: false });
      if (e) throw e;
      setComunicacoes(data || []);
    } catch (e: any) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar comunicações.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [empresaId]);

  const enviar = async () => {
    if (!assuntoForm.trim() && tipoForm === 'email') return;
    if (!conteudoForm.trim()) return;
    setEnviando(true);
    try {
      // Salva no banco
      const { error: dbErr } = await supabase.from('comunicacoes').insert({
        empresa_id: empresaId,
        tipo: tipoForm,
        direcao: 'enviado',
        assunto: assuntoForm.trim() || null,
        conteudo: conteudoForm.trim(),
        data_envio: new Date().toISOString(),
        lida: true,
      });
      if (dbErr) throw dbErr;

      // Tenta enviar via Gmail se conectado
      try {
        await supabase.functions.invoke('crm-email', {
          body: {
            action: 'send',
            to: '',
            subject: assuntoForm.trim(),
            body: conteudoForm.trim(),
          },
        });
      } catch {
        // Se Gmail não conectado, apenas salvou no banco — ok
      }

      setAssuntoForm('');
      setConteudoForm('');
      setMostrarForm(false);
      await load();
      onDataChanged?.();
    } catch (e: any) {
      setError(e instanceof Error ? e.message : 'Erro ao enviar.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
          Comunicações ({comunicacoes.length})
        </h4>
        <button
          onClick={() => setMostrarForm(!mostrarForm)}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#E8610A] hover:text-[#C4500A] px-2 py-1 rounded-lg hover:bg-[#E8610A]/5 cursor-pointer whitespace-nowrap"
        >
          <i className={mostrarForm ? 'ri-close-line' : 'ri-add-line'}></i>
          {mostrarForm ? 'Cancelar' : 'Nova comunicação'}
        </button>
      </div>

      {mostrarForm && (
        <div className="bg-gray-50 rounded-xl border border-hop-border p-4 space-y-3">
          <div className="flex gap-2">
            {(['email', 'whatsapp', 'ligacao', 'nota', 'reuniao'] as const).map((t) => {
              const cfg = TIPO_CONFIG[t];
              return (
                <button
                  key={t}
                  onClick={() => setTipoForm(t)}
                  className={`inline-flex items-center gap-1 text-xs font-semibold rounded-lg px-2 py-1.5 cursor-pointer whitespace-nowrap transition-colors ${
                    tipoForm === t
                      ? 'bg-[#E8610A] text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <i className={cfg.icon}></i>
                  {cfg.label}
                </button>
              );
            })}
          </div>
          {tipoForm === 'email' && (
            <input
              type="text"
              value={assuntoForm}
              onChange={(e) => setAssuntoForm(e.target.value)}
              placeholder="Assunto do e-mail..."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]"
            />
          )}
          <textarea
            value={conteudoForm}
            onChange={(e) => setConteudoForm(e.target.value)}
            placeholder="Digite a mensagem..."
            rows={3}
            maxLength={500}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] resize-none"
          />
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400">{conteudoForm.length}/500</span>
            <button
              onClick={enviar}
              disabled={enviando || (!assuntoForm.trim() && tipoForm === 'email') || !conteudoForm.trim()}
              className="inline-flex items-center gap-1.5 bg-[#E8610A] text-white text-xs font-semibold px-4 py-2 rounded-lg hover:bg-[#C4500A] cursor-pointer whitespace-nowrap disabled:opacity-60"
            >
              {enviando ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-send-plane-line"></i>}
              Enviar
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2">{error}</div>
      )}

      {loading && comunicacoes.length === 0 ? (
        <div className="flex justify-center py-8">
          <div className="w-6 h-6 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : comunicacoes.length === 0 ? (
        <p className="text-sm text-gray-400 py-4">Nenhuma comunicação registrada ainda.</p>
      ) : (
        <div className="space-y-2">
          {comunicacoes.map((c) => {
            const cfg = TIPO_CONFIG[c.tipo] || TIPO_CONFIG.nota;
            return (
              <div key={c.id} className="flex items-start gap-3 border border-hop-border rounded-xl p-3 bg-white">
                <div className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 ${cfg.tone}`}>
                  <i className={`${cfg.icon} text-sm`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold text-gray-600">{cfg.label}</span>
                    <span className="text-[10px] text-gray-400">{formatDateTime(c.data_envio)}</span>
                    {c.direcao === 'recebido' && !c.lida && (
                      <span className="text-[10px] font-semibold text-blue-600 bg-blue-50 rounded-full px-2 py-0.5">Novo</span>
                    )}
                  </div>
                  {c.assunto && (
                    <p className="text-sm font-semibold text-gray-800 mt-0.5">{c.assunto}</p>
                  )}
                  {c.conteudo && (
                    <p className="text-sm text-gray-600 mt-0.5 whitespace-pre-wrap">{c.conteudo}</p>
                  )}
                  {c.destinatario_email && (
                    <p className="text-xs text-gray-400 mt-1">
                      <i className="ri-mail-line mr-1"></i>
                      {c.destinatario_email}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}